import { Server } from '@4players/fleet';
import { App, hasActivatedProduct } from 'app/core/app/app.model';
import type { ConsoleSection } from 'environments/environment.model';
import { isSectionEnabled } from 'app/core/environment/build-features';
import {
  FLEET_RESOURCE_SECTIONS,
  FleetResource,
} from 'app/core/fleet/fleet-resources';
import { PaletteCommand } from './command-palette.model';
import { PALETTE_ACTION_CREATE, actionLink } from './palette-action';

/**
 * The command palette's registry.
 *
 * Everything here mirrors a destination that already exists in a sidebar —
 * `FleetSidebarComponent`, `VoiceSidebarComponent`, `RoomsSidebarComponent`,
 * `CortexSidebarComponent`, `AppSettingsComponent.menuData$` and
 * `UserSettingsComponent.panels`. When one of those grows an entry, add it here
 * too; the palette has no way to discover routes on its own.
 *
 * One exception, and it is the direction this should grow: Fleet's tutorials
 * and documentation links are read from `core/fleet/fleet-resources.ts`, which
 * the Learn page renders from as well. Hand-mirroring works for a route, whose
 * path is short and stable; it works badly for a set of links carrying titles,
 * icons, search keywords and a URL that changes per build.
 *
 * Two filters decide what a given user actually sees, and they are not the same
 * question:
 *
 * - `isSectionEnabled(...)` — whether this *build* can reach the section at all
 *   (staging is Fleet-only). Matches the `sectionEnabledGuard` on the route.
 * - `hasActivatedProduct(...)` — whether this *project* has the product switched
 *   on. Matches `productActivationGuard`.
 *
 * Offering a command that either guard would immediately bounce is worse than
 * not offering it, so both are applied up front.
 *
 * Note what is deliberately absent: none of these carry the project name. The
 * palette's scope chip says which project the list belongs to, so repeating it
 * on every row only made ~40 identical lines of text.
 */

/** Display names for the product slugs, in the order the console lists them. */
const PRODUCT_NAMES: Record<string, string> = {
  voice: 'Voice',
  rooms: 'Rooms',
  fleet: 'Fleet',
  cortex: 'Cortex',
};

/**
 * `Voice · Fleet · Cortex` for a project, or `undefined` when it runs nothing
 * this console knows about.
 *
 * Only the four curated slugs are named: the Payment catalog can hold others
 * (`amanda`, …) that have no page here, and listing a product the palette
 * cannot reach would be a promise it can't keep.
 */
function activatedProductNames(app: App): string | undefined {
  const names = Object.entries(PRODUCT_NAMES)
    .filter(([slug]) => hasActivatedProduct(app, slug))
    .map(([, name]) => name);

  return names.length ? names.join(' · ') : undefined;
}

/** Fleet setup facts known by the palette for one app. */
interface FleetCommandOptions {
  hasImages?: boolean;
  hasServerConfigs?: boolean;
  /** The project's servers, from `FleetServerIndexService`. */
  servers?: readonly Server[];
  /**
   * Whether the query is worth listing individual servers for.
   *
   * Unsearched they are left out entirely: a project with forty servers would
   * bury Fleet's twelve pages under them, and the palette's job with an empty
   * query is to show where you can go, not everything that exists. The
   * `Servers` row carries the count instead, which is the hint that searching
   * reaches them. Same rule the project list follows, only stricter — see the
   * preview cap in `command-palette.component.ts`.
   */
  searching?: boolean;
}

/**
 * One row per running server, so a server detail page is one search away
 * instead of four clicks — Fleet, Servers, find the row, open it.
 *
 * They live in the Fleet group rather than one of their own: a server is a
 * Fleet thing, and a separate heading would say otherwise. Which project a
 * server belongs to needs no work here — the palette already puts the project
 * name on any row outside the current filter, and falls back to this `meta`
 * inside it.
 *
 * The keywords carry everything a row cannot show. Rows are a single line, so
 * anyone searching by id, by the config a server came from, or by where it
 * runs has nothing else to match against.
 */
function serverCommands(
  app: App,
  options: FleetCommandOptions,
): PaletteCommand[] {
  if (!options.searching || !options.servers?.length) return [];

  return options.servers.map((server) => ({
    id: `${app.id}:fleet.server.${server.id}`,
    group: 'Fleet',
    // Led by the server config, trailed by the id: `Nginx without mounts #827`.
    //
    // Neither half works alone. The detail page calls itself `Server 827`, but
    // that id appears in no list the customer ever reads — the Servers table
    // shows region, instance number, config, address and status, and nothing
    // else — so as a row title it names the one thing nobody can search for.
    // The config is what they named themselves and what they mean by "my nginx
    // server". The id comes back on the end because several servers commonly
    // share one config, and without it those rows are indistinguishable.
    //
    // `server.name` stays out of the title: it is a fifty-character machine id
    // (`gs-uid_1-fid_2288609-aid_94-scid_126-…`) sharing a prefix with every
    // other server in the project. Searchable below, unreadable here.
    title: server.serverConfigName
      ? `${server.serverConfigName} #${server.id}`
      : `Server ${server.id}`,
    // Where it runs is what tells two rows apart inside one project. Across
    // projects the palette replaces this with the project name, which is the
    // more useful distinction there.
    meta: server.location?.cityDisplay ?? `#${server.id}`,
    icon: 'heroicons_outline:server',
    keywords: [
      String(server.id),
      // The old row title, so anyone who learned it — or who reads it off the
      // detail page — still finds the server by it.
      `server ${server.id}`,
      server.name ?? '',
      server.serverConfigName ?? '',
      server.status ?? '',
      server.location?.cityDisplay ?? '',
      server.location?.country ?? '',
      server.dns ?? '',
      'server instance',
    ]
      .filter(Boolean)
      .join(' '),
    // A server id belongs to one project and names it implicitly, so this row
    // is findable without the user knowing which project to say first.
    reachableAcrossProjects: true,
    routerLink: `/projects/${app.id}/fleet/server/${server.id}/info`,
  }));
}

/** Fleet sub-pages, mirroring `FleetSidebarComponent`. */
function fleetCommands(
  app: App,
  options: FleetCommandOptions = {},
): PaletteCommand[] {
  const base = `/projects/${app.id}/fleet`;
  const group = 'Fleet';

  return [
    {
      // `metadata`, `tags` and `labels` stay in the keywords on purpose: the
      // page moved and was renamed, but anyone whose muscle memory is
      // "⌘K → metadata" should still land where the editor now lives.
      id: 'fleet.app',
      group,
      title: 'App',
      icon: 'heroicons_outline:identification',
      keywords: 'fleet app id status maintenance metadata tags labels plan',
      routerLink: `${base}/app`,
    },
    {
      id: 'fleet.analytics',
      group,
      title: 'Analytics',
      icon: 'heroicons_outline:chart-bar',
      keywords: 'fleet overview dashboard charts usage',
      routerLink: `${base}/analytics`,
    },
    {
      id: 'fleet.explorer',
      group,
      title: 'Logs & Stats',
      icon: 'heroicons_outline:signal',
      keywords: 'fleet explorer monitoring metrics console output',
      routerLink: `${base}/explorer`,
    },
    {
      id: 'fleet.binary',
      group,
      title: 'Images',
      icon: 'heroicons_outline:cube',
      keywords: 'fleet binary binaries docker container registry',
      routerLink: `${base}/binary`,
    },
    {
      id: 'fleet.binary.create',
      group,
      title: 'Create Image',
      icon: 'heroicons_outline:plus-circle',
      keywords: 'fleet new binary docker upload steamworks repository',
      routerLink: `${base}/binary/create`,
    },
    {
      id: 'fleet.configs',
      group,
      title: 'Server Configs',
      icon: 'heroicons_outline:adjustments-vertical',
      keywords: 'fleet configuration ports env variables restart policy',
      routerLink: `${base}/configs`,
    },
    {
      id: 'fleet.configs.create',
      group,
      title: 'Create Server Config',
      icon: 'heroicons_outline:plus-circle',
      keywords: 'fleet new configuration',
      routerLink: `${base}/configs/create`,
      disabled: options.hasImages === false,
      disabledHint: options.hasImages === false ? 'No image.' : undefined,
    },
    {
      id: 'fleet.deployment',
      group,
      title: 'Deployments',
      icon: 'heroicons_outline:rocket-launch',
      keywords: 'fleet deploy release rollout locations regions',
      routerLink: `${base}/deployment`,
    },
    {
      // Not a page — the Deployments page's own dialog, opened on arrival. See
      // `palette-action.ts` for why this travels as a query parameter.
      id: 'fleet.deployment.create',
      group,
      title: 'Create Deployment',
      icon: 'heroicons_outline:plus-circle',
      keywords: 'fleet create new deployment location region deploy allocate',
      routerLink: actionLink(`${base}/deployment`, PALETTE_ACTION_CREATE),
      disabled: options.hasServerConfigs === false,
      disabledHint:
        options.hasServerConfigs === false ? 'No server config.' : undefined,
    },
    {
      id: 'fleet.server',
      group,
      title: 'Servers',
      // The count is the only sign that the servers themselves are searchable,
      // since none of them are listed until something is typed.
      meta: options.servers?.length
        ? String(options.servers.length)
        : undefined,
      icon: 'heroicons_outline:server',
      keywords: 'fleet instances game servers running allocate',
      routerLink: `${base}/server`,
    },
    ...serverCommands(app, options),
    {
      id: 'fleet.get-started',
      group,
      separatorBefore: true,
      title: 'Get Started with Fleet',
      icon: 'heroicons_outline:flag',
      keywords:
        'fleet setup onboarding quickstart first server image config deploy',
      routerLink: `${base}/get-started`,
    },
    {
      id: 'fleet.learn',
      group,
      title: 'Learn',
      icon: 'heroicons_outline:academic-cap',
      keywords: 'fleet tutorials documentation docs guide reference resources',
      routerLink: `${base}/learn`,
    },
    // The tutorials, from the same list the Learn page renders. The palette has
    // no `Tutorials` heading to carry the word, so unlike that page these keep
    // "Tutorial" in the title.
    ...tutorialResources().map((resource) => ({
      id: resource.id,
      group,
      title: `${resource.title} Tutorial`,
      onlyOnDirectMatch: true,
      icon: resource.icon,
      keywords: resource.keywords,
      routerLink: `${base}/${resource.path}`,
    })),
  ];
}

/** The `Tutorials` section of {@link FLEET_RESOURCE_SECTIONS}. */
function tutorialResources(): FleetResource[] {
  return (
    FLEET_RESOURCE_SECTIONS.find((section) => section.title === 'Tutorials')
      ?.resources ?? []
  );
}

/** Voice sub-pages, mirroring `VoiceSidebarComponent`. */
function voiceCommands(app: App): PaletteCommand[] {
  const base = `/projects/${app.id}/voice`;
  const group = 'Voice';

  const getStarted: PaletteCommand = {
    id: 'voice.get-started',
    group,
    title: 'Get Started with Voice',
    icon: 'heroicons_outline:flag',
    keywords: 'voice setup sdk unity unreal web download token quickstart',
    routerLink: `${base}/get-started`,
  };

  // On-premise projects run their own ODIN server: no usage, keys or limit
  // here, but its license and config (see voice.routes.ts).
  if (app.hostingType === 'on-premise') {
    return [
      {
        id: 'voice.overview',
        group,
        title: 'Voice Overview',
        icon: 'heroicons_outline:chart-bar',
        keywords: 'voice on-premise servers instances telemetry usage',
        routerLink: `${base}/overview`,
      },
      {
        id: 'voice.server',
        group,
        title: 'Voice Server',
        icon: 'heroicons_outline:server',
        keywords: 'voice on-premise license download config toml gateway',
        routerLink: `${base}/server`,
      },
      getStarted,
    ];
  }

  return [
    {
      id: 'voice.overview',
      group,
      title: 'Voice Overview',
      icon: 'heroicons_outline:chart-bar',
      keywords: 'voice analytics dashboard charts usage users rooms peak',
      routerLink: `${base}/overview`,
    },
    getStarted,
    {
      id: 'voice.access-keys',
      group,
      title: 'Access Keys',
      icon: 'heroicons_outline:key',
      keywords: 'voice odin credentials token generate room key register check',
      routerLink: `${base}/access-keys`,
    },
    {
      id: 'voice.access-keys.create',
      group,
      title: 'Create Access Key',
      icon: 'heroicons_outline:plus-circle',
      keywords: 'voice new access key generate credentials token',
      routerLink: actionLink(`${base}/access-keys`, PALETTE_ACTION_CREATE),
    },
    {
      id: 'voice.limits',
      group,
      title: 'Voice Limits',
      icon: 'heroicons_outline:adjustments-horizontal',
      keywords: 'voice budget peer limit concurrent users cap',
      routerLink: `${base}/limits`,
    },
  ];
}

/** Rooms sub-pages, mirroring `RoomsSidebarComponent`. */
function roomsCommands(app: App): PaletteCommand[] {
  const base = `/projects/${app.id}/rooms`;
  const group = 'Rooms';

  return [
    {
      id: 'rooms.analytics',
      group,
      title: 'Rooms Analytics',
      icon: 'heroicons_outline:chart-bar',
      keywords: 'rooms overview dashboard charts usage',
      routerLink: `${base}/analytics`,
    },
    {
      id: 'rooms.details',
      group,
      title: 'Project Details',
      icon: 'heroicons_outline:cog-6-tooth',
      keywords: 'rooms domain configuration settings',
      routerLink: `${base}/details`,
    },
  ];
}

/** Cortex sub-pages, mirroring `CortexSidebarComponent`. */
function cortexCommands(app: App): PaletteCommand[] {
  const base = `/projects/${app.id}/cortex`;
  const group = 'Cortex';

  return [
    {
      id: 'cortex.analytics',
      group,
      title: 'Cortex Analytics',
      icon: 'heroicons_outline:chart-bar',
      keywords: 'cortex overview dashboard charts usage transcription',
      routerLink: `${base}/analytics`,
    },
    {
      id: 'cortex.sessions',
      group,
      title: 'Sessions',
      icon: 'heroicons_outline:chat-bubble-left-right',
      keywords: 'cortex conversations transcripts messages rooms live',
      routerLink: `${base}/sessions`,
    },
    {
      id: 'cortex.participants',
      group,
      title: 'Participants',
      icon: 'heroicons_outline:users',
      keywords: 'cortex users players speakers',
      routerLink: `${base}/participants`,
    },
    {
      id: 'cortex.gatherings',
      group,
      title: 'Gatherings',
      icon: 'heroicons_outline:user-group',
      keywords: 'cortex groups clans squads teams',
      routerLink: `${base}/gatherings`,
    },
    {
      id: 'cortex.sanctions',
      group,
      title: 'Sanctions',
      icon: 'heroicons_outline:shield-exclamation',
      keywords: 'cortex moderation bans mutes kicks punishment',
      routerLink: `${base}/sanctions`,
    },
    {
      id: 'cortex.event-log',
      group,
      title: 'Event Logs',
      icon: 'heroicons_outline:inbox-stack',
      keywords: 'cortex events audit history sse stream',
      routerLink: `${base}/event-log`,
    },
    {
      id: 'cortex.functions',
      group,
      title: 'Functions',
      icon: 'heroicons_outline:code-bracket',
      keywords: 'cortex lab serverless handlers scripts automation code',
      routerLink: `${base}/functions`,
    },
  ];
}

/** Project settings panels, mirroring `AppSettingsComponent.menuData$`. */
function projectSettingsCommands(app: App): PaletteCommand[] {
  const base = `/projects/${app.id}/settings`;
  const group = 'Project Settings';

  return [
    {
      id: 'settings.overview',
      group,
      title: 'Overview',
      icon: 'heroicons_outline:presentation-chart-line',
      keywords: 'project summary home dashboard',
      routerLink: `${base}/overview`,
    },
    {
      id: 'settings.consumption',
      group,
      title: 'Usage & Costs',
      icon: 'heroicons_outline:chart-bar',
      keywords: 'consumption spend invoice minutes quota',
      routerLink: `${base}/consumption`,
    },
    {
      id: 'settings.products',
      group,
      title: 'Products',
      icon: 'heroicons_outline:cube-transparent',
      keywords: 'activate voice rooms fleet cortex enable',
      routerLink: `${base}/products`,
    },
    {
      id: 'settings.team',
      group,
      title: 'Team Management',
      icon: 'heroicons_outline:user-group',
      keywords: 'members invite roles permissions users',
      routerLink: `${base}/team`,
    },
    {
      id: 'settings.plan',
      group,
      title: 'Plan & Billing',
      icon: 'heroicons_outline:credit-card',
      keywords: 'subscription payment upgrade tier invoice',
      routerLink: `${base}/plan`,
    },
    {
      id: 'settings.token-generation',
      group,
      title: 'Token Generation',
      icon: 'heroicons_outline:key',
      keywords: 'access key webservice bot token provider',
      routerLink: `${base}/token-generation`,
    },
    {
      id: 'settings.bot-settings',
      group,
      title: 'Bot Settings',
      icon: 'heroicons_outline:cpu-chip',
      keywords: 'transcription bot encryption e2e sdk version debug audio',
      routerLink: `${base}/bot-settings`,
    },
    {
      id: 'settings.webhooks',
      group,
      title: 'Webhooks',
      icon: 'heroicons_outline:bell',
      keywords: 'events callbacks integrations notifications',
      routerLink: `${base}/webhooks`,
    },
    {
      id: 'settings.webhooks.create',
      group,
      title: 'Create Webhook',
      icon: 'heroicons_outline:plus-circle',
      keywords: 'new webhook subscription event callback endpoint',
      routerLink: actionLink(`${base}/webhooks`, PALETTE_ACTION_CREATE),
    },
    {
      id: 'settings.plugins',
      group,
      title: 'Plugins',
      icon: 'heroicons_outline:puzzle-piece',
      keywords: 'extensions moderation analytics automation integration',
      routerLink: `${base}/plugins`,
    },
    {
      id: 'settings.secret',
      group,
      title: 'Project Secret',
      icon: 'heroicons_outline:shield-check',
      keywords: 'signing secret credentials rotate',
      routerLink: `${base}/secret`,
    },
  ];
}

/**
 * Account-level settings, mirroring `UserSettingsComponent.panels`.
 *
 * The former one-line descriptions moved into `keywords`: they were the only
 * two-line rows left in the palette, and every one of them said roughly what
 * its title already said. Searching for "experimental" still finds Developer
 * Options.
 */
function accountSettingsCommands(): PaletteCommand[] {
  const group = 'Account';

  return [
    ...(isSectionEnabled('billing')
      ? [
          {
            id: 'account.billing',
            group,
            title: 'Billing',
            icon: 'heroicons_outline:credit-card',
            keywords:
              'invoice payment card address vat billing accounts payments',
            routerLink: '/settings/billing',
          },
        ]
      : []),
    {
      id: 'account.api-keys',
      group,
      title: 'API Keys (Cortex)',
      icon: 'heroicons_outline:key',
      keywords: 'cortex credentials secret token api keys',
      routerLink: '/settings/api-keys',
    },
    {
      id: 'account.api-keys.create',
      group,
      title: 'Create API Key',
      icon: 'heroicons_outline:plus-circle',
      keywords: 'new cortex api key credentials secret token generate',
      routerLink: actionLink('/settings/api-keys', PALETTE_ACTION_CREATE),
    },
    {
      id: 'account.tokens',
      group,
      title: 'Tokens (Fleet)',
      icon: 'heroicons_outline:finger-print',
      keywords: 'fleet pat personal access token cli login',
      routerLink: '/settings/tokens',
    },
    {
      id: 'account.docker-registries',
      group,
      title: 'Docker Registries',
      icon: 'heroicons_outline:signal',
      keywords: 'fleet registry credentials image pull private connections',
      routerLink: '/settings/docker-registries',
    },
    {
      id: 'account.developer-options',
      group,
      title: 'Developer Options',
      icon: 'heroicons_outline:code-bracket',
      keywords: 'experimental flags debug beta toggle features',
      routerLink: '/settings/developer-options',
    },
  ];
}

/**
 * Every destination inside one project, filtered by what this build can reach
 * and what this project has switched on.
 *
 * The four product blocks are gated identically — the only thing that differs
 * between them is the slug — so a fifth product is a one-line addition here
 * plus its own builder above.
 */
export function buildProjectCommands(
  app: App | null,
  options: FleetCommandOptions = {},
): PaletteCommand[] {
  if (!app) {
    return [];
  }

  const commands: PaletteCommand[] = [];

  if (isSectionEnabled('voice') && hasActivatedProduct(app, 'voice')) {
    commands.push(...voiceCommands(app));
  }
  if (isSectionEnabled('rooms') && hasActivatedProduct(app, 'rooms')) {
    commands.push(...roomsCommands(app));
  }
  if (isSectionEnabled('fleet') && hasActivatedProduct(app, 'fleet')) {
    commands.push(...fleetCommands(app, options));
  }
  if (isSectionEnabled('cortex') && hasActivatedProduct(app, 'cortex')) {
    commands.push(...cortexCommands(app));
  }
  if (isSectionEnabled('project-settings')) {
    commands.push(...projectSettingsCommands(app));
  }

  // Tag every command with its project, and namespace its id. The palette holds
  // several projects' pages at once, so `fleet.servers` on its own is no longer
  // an identity — and `appName` is what makes `alpine servers` find Alpine's
  // servers from inside another project.
  return commands.map((command) => ({
    ...command,
    id: `${app.id}:${command.id}`,
    appId: app.id,
    appName: app.name,
  }));
}

/**
 * Everything that does not belong to the scoped project: the account-level
 * settings, and one row per project.
 *
 * The project rows are what makes the palette app-wide. They are always in the
 * candidate pool — even while a project is scoped — so typing another project's
 * name is never a dead end: the `Projects` group simply outranks the scoped
 * project's own pages and floats to the top.
 *
 * @param apps Every project the user has, already in memory via `AppService`.
 * @param scopeId The currently scoped project, left out of the list because
 *   scoping to where you already are does nothing.
 */
/**
 * The product reference material, listed once for the whole account.
 *
 * These destinations do not belong to a project. Alongside MCP settings, the
 * Fleet developer guide is the same page whether you reached it from one
 * project or another — and building them per project was a pretence with two
 * costs. They followed their project's name into results that had nothing to do
 * with them (searching a project called *Nginx Template* returned its CLI
 * reference, because the row carried the project's name as a searchable field),
 * and with the project filter removed they arrived once per project: eight
 * identical `Fleet Developer Guide` rows, told apart only by a project name
 * that means nothing for a link leaving the console.
 *
 * Gated per product, as before, only now at the level the links actually live
 * at: a customer sees Fleet's documentation when *any* of their projects has
 * Fleet, rather than once per project that does.
 */
function documentationCommands(apps: App[]): PaletteCommand[] {
  const group = 'Documentation';
  const runs = (product: ConsoleSection): boolean =>
    isSectionEnabled(product) &&
    apps.some((app) => hasActivatedProduct(app, product));

  return [
    // Fleet uses the same resources as Learn — one source, so the
    // API link cannot end up pointing at the wrong environment in one of the
    // two places. Titles gain the product name here, which the page gets from
    // being inside Fleet already.
    ...(runs('fleet')
      ? FLEET_RESOURCE_SECTIONS.find(
          (section) => section.title === 'Documentation',
        )!.resources.map((resource) => ({
          id: resource.id,
          group,
          title: `Fleet ${resource.title}`,
          icon: resource.icon,
          keywords: resource.keywords,
          ...(resource.path
            ? { routerLink: resource.path }
            : { meta: 'docs.4players.io', url: resource.url }),
        }))
      : []),
    ...(runs('voice')
      ? [
          {
            id: 'voice.docs.developer',
            group,
            title: 'Voice Developer Guide',
            meta: 'docs.4players.io',
            icon: 'heroicons_outline:book-open',
            keywords: 'voice documentation docs help manual sdk',
            url: 'https://docs.4players.io/voice/',
          },
        ]
      : []),
    ...(runs('rooms')
      ? [
          {
            id: 'rooms.docs.developer',
            group,
            title: 'Rooms Developer Guide',
            meta: 'docs.4players.io',
            icon: 'heroicons_outline:book-open',
            keywords: 'rooms documentation docs help manual sdk',
            url: 'https://docs.4players.io/rooms/',
          },
        ]
      : []),
    ...(runs('cortex')
      ? [
          {
            id: 'cortex.docs.developer',
            group,
            title: 'Cortex Developer Guide',
            meta: 'docs.4players.io',
            icon: 'heroicons_outline:book-open',
            keywords: 'cortex documentation docs help manual sdk',
            url: 'https://docs.4players.io/cortex',
          },
        ]
      : []),
  ];
}

export function buildGlobalCommands(
  apps: App[],
  scopeId: string | null,
): PaletteCommand[] {
  const projects: PaletteCommand[] = apps
    .filter((app) => app.id !== scopeId)
    .map((app) => ({
      id: `project.${app.id}`,
      group: 'Projects',
      title: app.name,
      // What the project *is*, rather than its id. In a Payment build the id is
      // a base64 blob — 32 unreadable characters that tell nobody anything and
      // push the name out of the row. Two projects with the same name are told
      // apart by what they run. The id stays searchable below.
      meta: activatedProductNames(app),
      icon: 'heroicons_outline:cube',
      keywords: `${app.id} ${app.description ?? ''}`,
      scopeAppId: app.id,
    }));

  return [
    ...accountSettingsCommands(),
    {
      id: 'console.projects',
      group: 'Console',
      title: 'All Projects',
      icon: 'heroicons_outline:squares-2x2',
      keywords: 'projects list apps switch overview home manage',
      routerLink: '/projects',
    },
    ...(isSectionEnabled('project-creation')
      ? [
          {
            id: 'console.projects.create',
            group: 'Console',
            title: 'New Project',
            icon: 'heroicons_outline:plus-circle',
            keywords: 'create project app add',
            routerLink: '/projects/new',
          },
        ]
      : []),
    ...documentationCommands(apps),
    ...projects,
  ];
}
